const _Connection_handlers = Symbol("#handlers"),
      _Connection_mustBeClosed = Symbol("#mustBeClosed"),
      _Connection_reopenPeriod = Symbol("#reopenPeriod"),
      _Connection_reopening = Symbol("#reopening"),
      _Connection_socket = Symbol("#socket"),
      _Connection_url = Symbol("#url"),
      _Connection_openPromise = Symbol("#openPromise"),
      _Connection_reopenPeriodResetTimer = Symbol("#reopenPeriodResetTimer"),
      _Connection_resolveDelayedOpenPromise = Symbol("#resolveDelayedOpenPromise"),
      _Connection_tryToReopen = Symbol("#tryToReopen"),
      _Connection_sendIfOpen = Symbol("#sendIfOpen"),
      _Connection_removeEvents = Symbol("#removeEvents");

// import type { SelfDebug } from './self-debug';
import { addEvents } from "../helpers/add-events.js";
import { ensureDefined } from "../helpers/ensure-defined.js";
const eventKeys = ['open', 'message', 'error', 'close'];
const stateCommandMap = {
  cancel: 'X',
  force: 'F'
};
export class Connection {
  constructor(url,
  /* selfDebug: SelfDebug|null, */
  {
    delayedOpen
  } = {}) {
    // readonly #selfDebug: SelfDebug|null;
    this[_Connection_handlers] = {
      open: [],
      message: [],
      error: [],
      close: []
    };
    this[_Connection_mustBeClosed] = false;
    this[_Connection_reopenPeriod] = 0;
    this[_Connection_reopening] = false;

    this.open = () => {
      this[_Connection_socket] = new WebSocket(this[_Connection_url]);
      this[_Connection_openPromise] = new Promise(resolve => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        this[_Connection_socket].addEventListener('open', () => {
          this[_Connection_reopenPeriodResetTimer] = setTimeout(() => {
            this[_Connection_reopenPeriod] = 0;
          }, this[_Connection_reopenPeriod]);

          if (this[_Connection_resolveDelayedOpenPromise]) {
            this[_Connection_resolveDelayedOpenPromise]();

            this[_Connection_resolveDelayedOpenPromise] = null;
          }

          resolve();
        });
      });

      for (const key of eventKeys) {
        const handlersByKey = this[_Connection_handlers][key];

        this[_Connection_socket].addEventListener(key, e => {
          const handlerArguments = [e];

          if (key === 'message') {
            const data = JSON.parse(e.data);

            if (data.type === 'self:debug') {
              for (const entry of data.log) {
                entry.time = new Date(entry.time);
              }
            } // if (this.#selfDebug)
            //     this.#selfDebug.log('before', JSON.stringify(data));


            handlerArguments.unshift(data);
          }

          for (const handler of handlersByKey) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-call
            handler(...handlerArguments);
          } // if (this.#selfDebug && key === 'message')
          //     this.#selfDebug.log('after', JSON.stringify(handlerArguments[0]));

        });
      }
    };

    this[_Connection_tryToReopen] = () => {
      if (this[_Connection_mustBeClosed] || this[_Connection_reopening]) return;

      if (this[_Connection_reopenPeriodResetTimer]) {
        clearTimeout(this[_Connection_reopenPeriodResetTimer]);
        this[_Connection_reopenPeriodResetTimer] = null;
      }

      this[_Connection_reopening] = true;
      setTimeout(() => {
        this.open();
        this[_Connection_reopening] = false;
      }, this[_Connection_reopenPeriod]);
      if (this[_Connection_reopenPeriod] < 60000) this[_Connection_reopenPeriod] = Math.min(5 * (this[_Connection_reopenPeriod] + 200), 60000);
    };

    this[_Connection_sendIfOpen] = async command => {
      if (this[_Connection_mustBeClosed]) throw `Cannot send command '${command}' after the close() call.`;

      if (this[_Connection_socket]?.readyState !== WebSocket.OPEN) {
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        console.warn(`Dropped command '${command}' because the socket state is ${this[_Connection_socket]?.readyState}.`);
        return;
      } // if (this.#selfDebug)
      //     this.#selfDebug.log('send', command);


      this[_Connection_socket].send(command);
    };

    this[_Connection_url] = url; // this.#selfDebug = selfDebug;

    if (!delayedOpen) {
      this.open();
    } else {
      this[_Connection_openPromise] = new Promise(resolve => {
        this[_Connection_resolveDelayedOpenPromise] = resolve;
      });
    }

    this[_Connection_removeEvents] = addEvents(this, {
      error: this[_Connection_tryToReopen],
      close: this[_Connection_tryToReopen]
    });
  }

  on(key, handler) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-call
    this[_Connection_handlers][key].push(handler);
  }

  off(key, handler) {
    const list = this[_Connection_handlers][key]; // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-call

    const index = list.indexOf(handler);
    if (index >= 0) list.splice(index, 1);
  }

  sendReplaceText(start, length, newText, cursorIndexAfter, reason) {
    return this[_Connection_sendIfOpen]('R' + start + ':' + length + ':' + cursorIndexAfter + ':' + (reason ?? '') + ':' + newText);
  }

  sendMoveCursor(cursorIndex) {
    return this[_Connection_sendIfOpen]('M' + cursorIndex);
  }

  sendTypeChar(char) {
    return this[_Connection_sendIfOpen]('C' + char);
  }

  sendCompletionState(indexOrCommand, indexIfInfo) {
    // common bug -- the specific flow is not fully clear yet,
    // but there is no reason to send null/undefined to server if it will fail anyway
    ensureDefined(indexOrCommand, 'completion command');
    const argument = indexOrCommand !== 'info' ? stateCommandMap[indexOrCommand] ?? indexOrCommand : 'I' + ensureDefined(indexIfInfo, 'completion info index');
    return this[_Connection_sendIfOpen]('S' + argument);
  }

  sendSignatureHelpState(command) {
    return this[_Connection_sendIfOpen]('P' + stateCommandMap[command]);
  }

  sendRequestInfoTip(cursorIndex) {
    return this[_Connection_sendIfOpen]('I' + cursorIndex);
  }

  sendSlowUpdate() {
    return this[_Connection_sendIfOpen]('U');
  }

  sendApplyDiagnosticAction(actionId) {
    return this[_Connection_sendIfOpen]('F' + actionId);
  }

  sendSetOptions(options) {
    const optionPairs = [];

    for (const key in options) {
      optionPairs.push(key + '=' + options[key]);
    }

    return this[_Connection_sendIfOpen]('O' + optionPairs.join(','));
  }

  sendRequestSelfDebugData() {
    return this[_Connection_sendIfOpen]('Y');
  }

  close() {
    this[_Connection_mustBeClosed] = true;

    this[_Connection_removeEvents]();

    this[_Connection_socket]?.close();
  }

}